import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Button, Typography, Box } from '@mui/material';

const CameraPermissionScreen = ({ setCameraStream }) => {
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const [permissionState, setPermissionState] = useState('prompt');
  const navigate = useNavigate();
  const [hasRequested, setHasRequested] = useState(false);

  const checkPermission = async () => {
    try {
      const permission = await navigator.permissions.query({ name: 'camera' });
      console.log('Estado del permiso de cámara:', permission.state);
      return permission.state;
    } catch (err) {
      console.error('Error al verificar permisos:', err);
      return 'prompt';
    }
  };

  const requestCameraPermission = async () => {
    if (hasRequested) return;
    setHasRequested(true);
    setIsChecking(true);
    setError(null);
    console.log('Solicitando permiso de cámara...');
  
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('El navegador no soporta acceso a la cámara');
      }
  
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      console.log('Dispositivos de video encontrados:', videoDevices);
  
      if (videoDevices.length === 0) {
        throw new Error('No se encontraron cámaras en este dispositivo');
      }
  
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Simplificar para prueba
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };
  
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Stream creado exitosamente:', stream);
      const tracks = stream.getVideoTracks();
      console.log('Tracks de video:', tracks);
      tracks.forEach(track => {
        console.log(`Track ${track.label}: enabled=${track.enabled}, readyState=${track.readyState}`);
      });
  
      if (tracks.length === 0 || !tracks[0].enabled || tracks[0].readyState !== 'live') {
        throw new Error('El stream no contiene un track de video activo');
      }
  
      setCameraStream(stream);
      setIsChecking(false);
      setPermissionState('granted');
      console.log('Stream asignado, navegando a /form');
      navigate('/form');
    } catch (err) {
      console.error('Error al obtener acceso a la cámara:', err);
      setError(`Error: ${err.message}. Verifica los permisos o prueba con otro dispositivo.`);
      setPermissionState(err.name === 'NotAllowedError' ? 'denied' : 'prompt');
      setIsChecking(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeCamera = async () => {
      const state = await checkPermission();
      console.log('Inicializando cámara con estado:', state);
      if (mounted && !hasRequested) {
        requestCameraPermission();
      }
    };

    initializeCamera();

    return () => {
      mounted = false;
    };
  }, []);

  const handleRetry = () => {
    setError(null);
    setHasRequested(false);
    requestCameraPermission();
  };

  return (
    <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'white', color: 'black' }}>
      <Typography variant="h4" sx={{ color: 'black' }}>Permiso de Cámara</Typography>
      {isChecking ? (
        <Box>
          <CircularProgress sx={{ color: 'red', mt: 2 }} />
          <Typography>Configurando la cámara...</Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body1" sx={{ color: error ? 'red' : 'black', mt: 2 }}>
            {error || 'Necesitamos acceder a tu cámara para continuar.'}
          </Typography>
          <Button
            variant="contained"
            onClick={handleRetry}
            sx={{ mt: 2, bgcolor: 'black', color: 'white', '&:hover': { bgcolor: '#333' } }}
          >
            {error ? 'Reintentar' : 'Permitir acceso'}
          </Button>
        </>
      )}
    </Box>
  );
};

export default CameraPermissionScreen;